import { color } from '../styles/theme';
import { fullDate, initials } from '../lib/format';
import type { AdminRow } from '../api/client';

/**
 * One staff account, as a table cell: initials, name, the YOU marker, an
 * INVITED or SUSPENDED pill when the account is not live, and the email.
 *
 * Shared by the Roles and Users tables, which otherwise differ in every
 * column — sharing the cell keeps the person recognisable across the two
 * screens without forcing one table shape on both.
 */
export function MemberIdentity({
  name,
  email,
  status,
  isMe = false,
  lastLoginAt,
}: {
  name: string;
  email: string;
  status: AdminRow['status'];
  isMe?: boolean;
  lastLoginAt?: string | null;
}) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
      <span
        style={{
          display: 'flex',
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: color.tint,
          color: color.blueDeep,
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 800,
          flex: 'none',
        }}
      >
        {initials(name)}
      </span>
      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 14,
            fontWeight: 700,
            color: color.ink,
            minWidth: 0,
          }}
        >
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </span>
          {isMe && (
            <span style={{ fontSize: 10.5, fontWeight: 800, color: color.muted }}>YOU</span>
          )}
          {status !== 'active' && <StatusPill status={status} />}
        </span>
        <span
          style={{
            display: 'block',
            fontSize: 12,
            color: color.muted,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={lastLoginAt ? `Last in ${fullDate(lastLoginAt)}` : undefined}
        >
          {email}
        </span>
      </span>
    </span>
  );
}

/** INVITED / SUSPENDED, on the grey the design uses for "not live". */
export function StatusPill({ status }: { status: AdminRow['status'] }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 800,
        background: color.line,
        // ink, not muted: muted on this grey is 4.47:1, just under
        // WCAG 1.4.3's 4.5.
        color: color.ink,
        padding: '2px 7px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {status.toUpperCase()}
    </span>
  );
}
